import Box from "@mui/material/Box";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import ArgonBox from "components/ArgonBox";
import ArgonTypography from "components/ArgonTypography";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CloseIcon from "@mui/icons-material/Close";
import PropTypes from "prop-types";
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";

const PRIMARY = "#1E86F6";
const PRIMARY_SOFT = "rgba(30, 134, 246, 0.12)";
const TEXT = "#344767";
const MUTED = "#8392ab";
const BORDER = "#e9ecef";
const PORTAL_Z = 20000;

const MONTHS_ES = [
  "enero", "febrero", "marzo", "abril", "mayo", "junio",
  "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre",
];

const WEEK_SHORT_ES = ["L", "M", "X", "J", "V", "S", "D"];

function parseIsoLocal(iso) {
  if (!iso || typeof iso !== "string") return null;
  const p = iso.split("-");
  if (p.length !== 3) return null;
  const y = Number(p[0], 10);
  const m = Number(p[1], 10);
  const d = Number(p[2], 10);
  if (!y || !m || !d || m < 1 || m > 12 || d < 1 || d > 31) return null;
  return { y, m: m - 1, d };
}

function toIso(y, monthIndex, day) {
  return `${y}-${String(monthIndex + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

function todayParts() {
  const n = new Date();
  return { y: n.getFullYear(), m: n.getMonth(), d: n.getDate() };
}

/** Lunes = 0 … Domingo = 6 */
function mondayBasedOffset(jsWeekday) {
  return (jsWeekday + 6) % 7;
}

function HistorialAccionesDatePicker({ id, value, onChange, minDate, maxDate, placeholder }) {
  const anchorRef = useRef(null);
  const panelRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [panelPos, setPanelPos] = useState({ top: 0, left: 0, width: 300 });
  const [viewY, setViewY] = useState(() => todayParts().y);
  const [viewM, setViewM] = useState(() => todayParts().m);

  const selected = useMemo(() => parseIsoLocal(value), [value]);

  const updatePanelPosition = useCallback(() => {
    const el = anchorRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const panelWidth = Math.max(280, Math.min(320, Math.max(r.width, 260)));
    let left = r.left;
    const margin = 8;
    if (left + panelWidth > window.innerWidth - margin) {
      left = Math.max(margin, window.innerWidth - panelWidth - margin);
    }
    if (left < margin) left = margin;
    setPanelPos({
      top: r.bottom + 8,
      left,
      width: panelWidth,
    });
  }, []);

  useLayoutEffect(() => {
    if (!open) return undefined;
    updatePanelPosition();
    const h = () => updatePanelPosition();
    window.addEventListener("scroll", h, true);
    window.addEventListener("resize", h);
    return () => {
      window.removeEventListener("scroll", h, true);
      window.removeEventListener("resize", h);
    };
  }, [open, updatePanelPosition]);

  useEffect(() => {
    if (!open) return undefined;
    const parts = selected || todayParts();
    setViewY(parts.y);
    setViewM(parts.m);
    return undefined;
  }, [open, selected]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      const t = e.target;
      if (anchorRef.current?.contains(t)) return;
      if (panelRef.current?.contains(t)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const displayText = useMemo(() => {
    if (!selected) return "";
    const { y, m, d } = selected;
    return `${String(d).padStart(2, "0")}/${String(m + 1).padStart(2, "0")}/${y}`;
  }, [selected]);

  const inRange = useCallback(
    (y, monthIndex, day) => {
      const iso = toIso(y, monthIndex, day);
      if (minDate && iso < minDate) return false;
      if (maxDate && iso > maxDate) return false;
      return true;
    },
    [minDate, maxDate]
  );

  const gridCells = useMemo(() => {
    const first = new Date(viewY, viewM, 1);
    const lead = mondayBasedOffset(first.getDay());
    const daysInMonth = new Date(viewY, viewM + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < lead; i += 1) cells.push({ type: "empty", key: `e-${i}` });
    for (let d = 1; d <= daysInMonth; d += 1) {
      cells.push({ type: "day", day: d, key: `d-${d}` });
    }
    while (cells.length % 7 !== 0) {
      cells.push({ type: "empty", key: `t-${cells.length}` });
    }
    return cells;
  }, [viewY, viewM]);

  const t = todayParts();
  const monthTitle = `${MONTHS_ES[viewM]} ${viewY}`;

  const goPrev = () => {
    if (viewM === 0) {
      setViewY((y) => y - 1);
      setViewM(11);
    } else {
      setViewM((m) => m - 1);
    }
  };

  const goNext = () => {
    if (viewM === 11) {
      setViewY((y) => y + 1);
      setViewM(0);
    } else {
      setViewM((m) => m + 1);
    }
  };

  const pickDay = (day) => {
    const iso = toIso(viewY, viewM, day);
    if (!inRange(viewY, viewM, day)) return;
    onChange(iso);
    setOpen(false);
  };

  const clear = (e) => {
    e.stopPropagation();
    onChange("");
  };

  return (
    <Box sx={{ position: "relative", width: "100%" }}>
      <Box
        ref={anchorRef}
        component="button"
        type="button"
        id={id}
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="dialog"
        aria-expanded={open}
        sx={{
          width: "100%",
          boxSizing: "border-box",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          fontFamily: '"Open Sans", "Helvetica", "Arial", sans-serif',
          fontSize: "0.875rem",
          fontWeight: 500,
          color: displayText ? TEXT : MUTED,
          backgroundColor: "#fff",
          backgroundImage: "none",
          border: `1px solid ${BORDER}`,
          borderRadius: "12px",
          px: 1.75,
          py: 1.25,
          cursor: "pointer",
          textAlign: "left",
          outline: "none",
          WebkitAppearance: "none",
          MozAppearance: "none",
          transition: "border-color 0.2s ease, box-shadow 0.2s ease",
          "&:hover": {
            borderColor: "#dee2e6",
            backgroundColor: "#fafcff",
          },
          ...(open
            ? {
                borderColor: PRIMARY,
                boxShadow: `0 0 0 3px ${PRIMARY_SOFT}`,
                backgroundColor: "#fff",
              }
            : {}),
        }}
      >
        <ArgonTypography
          component="span"
          sx={{
            flex: 1,
            fontSize: "inherit",
            fontWeight: "inherit",
            color: "inherit",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {displayText || placeholder}
        </ArgonTypography>
        <ArgonBox sx={{ display: "flex", alignItems: "center", gap: 0.25, flexShrink: 0 }}>
          {value ? (
            <ArgonBox
              component="span"
              role="button"
              tabIndex={0}
              onClick={clear}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  clear(e);
                }
              }}
              sx={{
                display: "inline-flex",
                p: 0.25,
                borderRadius: "6px",
                color: MUTED,
                cursor: "pointer",
                "&:hover": { color: TEXT, bgcolor: "rgba(0,0,0,0.04)" },
              }}
              aria-label="Quitar fecha"
            >
              <CloseIcon sx={{ fontSize: 18 }} />
            </ArgonBox>
          ) : null}
          <CalendarMonthOutlinedIcon sx={{ fontSize: 20, color: MUTED }} />
        </ArgonBox>
      </Box>

      {typeof document !== "undefined" && open
        ? createPortal(
            <Box
              ref={panelRef}
              role="dialog"
              aria-label="Calendario"
              sx={{
                position: "fixed",
                top: panelPos.top,
                left: panelPos.left,
                width: panelPos.width,
                zIndex: PORTAL_Z,
                maxHeight: "min(420px, calc(100vh - 16px))",
                overflowY: "auto",
                p: 1.5,
                borderRadius: "14px",
                backgroundColor: "#fff",
                border: `1px solid ${PRIMARY_SOFT}`,
                boxShadow: "0 18px 48px -12px rgba(52, 71, 103, 0.35)",
              }}
            >
          <ArgonBox display="flex" alignItems="center" justifyContent="space-between" mb={1.5}>
            <Box
              component="button"
              type="button"
              onClick={goPrev}
              sx={{
                border: "none",
                backgroundColor: "rgba(30, 134, 246, 0.1)",
                backgroundImage: "none",
                borderRadius: "10px",
                p: 0.5,
                cursor: "pointer",
                display: "flex",
                color: PRIMARY,
                WebkitAppearance: "none",
                "&:hover": { backgroundColor: "rgba(30, 134, 246, 0.18)" },
              }}
              aria-label="Mes anterior"
            >
              <ChevronLeftIcon />
            </Box>
            <ArgonTypography
              sx={{
                fontSize: "0.9rem",
                fontWeight: 700,
                color: TEXT,
                textTransform: "capitalize",
                textAlign: "center",
                flex: 1,
                px: 1,
              }}
            >
              {monthTitle}
            </ArgonTypography>
            <Box
              component="button"
              type="button"
              onClick={goNext}
              sx={{
                border: "none",
                backgroundColor: "rgba(30, 134, 246, 0.1)",
                backgroundImage: "none",
                borderRadius: "10px",
                p: 0.5,
                cursor: "pointer",
                display: "flex",
                color: PRIMARY,
                WebkitAppearance: "none",
                "&:hover": { backgroundColor: "rgba(30, 134, 246, 0.18)" },
              }}
              aria-label="Mes siguiente"
            >
              <ChevronRightIcon />
            </Box>
          </ArgonBox>

          <ArgonBox
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(7, 1fr)",
              gap: "4px",
              mb: 0.5,
            }}
          >
            {WEEK_SHORT_ES.map((w) => (
              <ArgonTypography
                key={w}
                sx={{
                  fontSize: "0.65rem",
                  fontWeight: 700,
                  color: MUTED,
                  textAlign: "center",
                  py: 0.5,
                  letterSpacing: "0.04em",
                }}
              >
                {w}
              </ArgonTypography>
            ))}
          </ArgonBox>

          <ArgonBox sx={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: "4px" }}>
            {gridCells.map((cell) => {
              if (cell.type === "empty") {
                return <ArgonBox key={cell.key} sx={{ minHeight: 36 }} />;
              }
              const { day } = cell;
              const iso = toIso(viewY, viewM, day);
              const allowed = inRange(viewY, viewM, day);
              const isSel = value === iso;
              const isToday = t.y === viewY && t.m === viewM && t.d === day;
              const daySx = (() => {
                if (!allowed) {
                  return {
                    minHeight: 38,
                    border: "1px solid transparent",
                    borderRadius: "10px",
                    fontSize: "0.8125rem",
                    fontWeight: 500,
                    fontFamily: "inherit",
                    cursor: "not-allowed",
                    backgroundColor: "#f8f9fa",
                    backgroundImage: "none",
                    color: "#adb5bd",
                    opacity: 0.85,
                    WebkitAppearance: "none",
                  };
                }
                if (isSel) {
                  return {
                    minHeight: 38,
                    border: `1px solid ${PRIMARY}`,
                    borderRadius: "10px",
                    fontSize: "0.8125rem",
                    fontWeight: 700,
                    fontFamily: "inherit",
                    cursor: "pointer",
                    backgroundColor: PRIMARY,
                    backgroundImage: "none",
                    color: "#fff",
                    boxShadow: "0 2px 8px rgba(30, 134, 246, 0.35)",
                    WebkitAppearance: "none",
                    transition: "transform 0.1s ease, box-shadow 0.15s ease",
                    "&:hover": {
                      backgroundColor: "#1976d2",
                      borderColor: "#1976d2",
                      color: "#fff",
                    },
                  };
                }
                return {
                  minHeight: 38,
                  border: "1px solid #e9ecef",
                  borderRadius: "10px",
                  fontSize: "0.8125rem",
                  fontWeight: 600,
                  fontFamily: "inherit",
                  cursor: "pointer",
                  backgroundColor: "#fff",
                  backgroundImage: "none",
                  color: TEXT,
                  WebkitAppearance: "none",
                  transition: "background-color 0.15s ease, border-color 0.15s ease, color 0.15s ease",
                  ...(isToday
                    ? { boxShadow: `inset 0 0 0 2px ${PRIMARY}` }
                    : {}),
                  "&:hover": {
                    backgroundColor: "#f0f7ff",
                    borderColor: PRIMARY,
                    color: PRIMARY,
                  },
                };
              })();
              return (
                <Box
                  key={cell.key}
                  component="button"
                  type="button"
                  disabled={!allowed}
                  onClick={() => pickDay(day)}
                  sx={daySx}
                >
                  {day}
                </Box>
              );
            })}
          </ArgonBox>
            </Box>,
            document.body
          )
        : null}
    </Box>
  );
}

HistorialAccionesDatePicker.propTypes = {
  id: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  minDate: PropTypes.string,
  maxDate: PropTypes.string,
  placeholder: PropTypes.string,
};

HistorialAccionesDatePicker.defaultProps = {
  minDate: undefined,
  maxDate: undefined,
  placeholder: "Elegir fecha",
};

export default HistorialAccionesDatePicker;
